import { TOOL_MATERIALS, TOOL_TYPES } from '@bridgewerk/shared';
import type { ToolLibraryRecord, ToolMaterial, ToolType } from '@bridgewerk/shared';
import { getApiClient, type CreateToolLibraryInput } from '../../lib/api-client.js';
import type {
  AssignToolArgs,
  CreateToolLibraryArgs,
  DeleteToolLibraryArgs,
  RemoveToolFromSlotArgs,
  SwapToolSlotsArgs,
  UpdateToolLibraryArgs,
  UnsupportedArgs,
  VoiceIntentResponse,
} from './types.js';

export interface ExecuteContext {
  readonly shopId: string;
  /** Library entries available for matching when args lack a UUID. */
  readonly library?: readonly ToolLibraryRecord[];
}

export type ExecuteResult =
  | { kind: 'created_library_entry'; record: ToolLibraryRecord }
  | { kind: 'updated_library_entry'; record: ToolLibraryRecord }
  | { kind: 'deleted_library_entry'; toolId: string; description: string }
  | { kind: 'assigned_slot'; machineId: string; toolNumber: number; toolLibraryId: string }
  | { kind: 'removed_slot'; machineId: string; toolNumber: number }
  | { kind: 'swapped_slots'; machineId: string; fromNum: number; toNum: number }
  | { kind: 'rejected'; reason: string };

const TOOL_TYPE_SET: ReadonlySet<string> = new Set(TOOL_TYPES);
const TOOL_MATERIAL_SET: ReadonlySet<string> = new Set(TOOL_MATERIALS);

export async function executeIntent(
  intent: VoiceIntentResponse,
  ctx: ExecuteContext,
): Promise<ExecuteResult> {
  switch (intent.tool) {
    case 'create_tool_library_entry': {
      const args = intent.args as unknown as CreateToolLibraryArgs;
      const input = normalizeCreate(args);
      const record = await getApiClient().createToolLibraryEntry(ctx.shopId, input);
      return { kind: 'created_library_entry', record };
    }

    case 'update_tool_library_entry': {
      const args = intent.args as unknown as UpdateToolLibraryArgs;
      const tool = matchLibraryTool(args.tool_description_hint, ctx.library);
      if (!tool) {
        return {
          kind: 'rejected',
          reason: `Could not find a library tool matching "${args.tool_description_hint}". Check the library and try again.`,
        };
      }
      // Merge: only override fields the LLM explicitly provided (not undefined)
      const input: CreateToolLibraryInput = {
        description:           args.description ?? tool.description,
        type:                  (args.type as ToolType | null | undefined) ?? tool.type,
        material:              (args.material as ToolMaterial | null | undefined) ?? tool.material,
        manufacturer:          args.manufacturer !== undefined ? args.manufacturer : tool.manufacturer,
        part_number:           args.part_number !== undefined ? args.part_number : tool.part_number,
        diameter_mm:           args.diameter_mm !== undefined ? args.diameter_mm : tool.diameter_mm,
        expected_life_cuts:    args.expected_life_cuts !== undefined ? args.expected_life_cuts : tool.expected_life_cuts,
        expected_life_minutes: args.expected_life_minutes !== undefined ? args.expected_life_minutes : tool.expected_life_minutes,
        notes:                 args.notes !== undefined ? args.notes : tool.notes,
      };
      const record = await getApiClient().updateToolLibraryEntry(ctx.shopId, tool.tool_id, input);
      return { kind: 'updated_library_entry', record };
    }

    case 'delete_tool_library_entry': {
      const args = intent.args as unknown as DeleteToolLibraryArgs;
      const tool = matchLibraryTool(args.tool_description_hint, ctx.library);
      if (!tool) {
        return {
          kind: 'rejected',
          reason: `Could not find a library tool matching "${args.tool_description_hint}". Check the library and try again.`,
        };
      }
      await getApiClient().deleteToolLibraryEntry(ctx.shopId, tool.tool_id);
      return { kind: 'deleted_library_entry', toolId: tool.tool_id, description: tool.description };
    }

    case 'assign_tool_to_slot': {
      const args = intent.args as unknown as AssignToolArgs;
      const libraryId = args.tool_library_id ?? matchLibrary(args.tool_description_hint, ctx.library);
      if (!libraryId) {
        return {
          kind: 'rejected',
          reason: args.tool_description_hint
            ? `Could not find a library tool matching "${args.tool_description_hint}". Add it to the library first.`
            : 'No tool selected to assign.',
        };
      }
      await getApiClient().assignToolToSlot(ctx.shopId, args.machine_id, args.tool_number, libraryId);
      return {
        kind: 'assigned_slot',
        machineId: args.machine_id,
        toolNumber: args.tool_number,
        toolLibraryId: libraryId,
      };
    }

    case 'remove_tool_from_slot': {
      const args = intent.args as unknown as RemoveToolFromSlotArgs;
      await getApiClient().unassignToolFromSlot(ctx.shopId, args.machine_id, args.tool_number);
      return { kind: 'removed_slot', machineId: args.machine_id, toolNumber: args.tool_number };
    }

    case 'swap_tool_slots': {
      const args = intent.args as unknown as SwapToolSlotsArgs;
      const toolList = await getApiClient().getMachineTools(ctx.shopId, args.machine_id);
      const fromSlot = toolList.tools.find(t => t.tool_number === args.from_tool_number) ?? null;
      const toSlot   = toolList.tools.find(t => t.tool_number === args.to_tool_number) ?? null;
      if (!fromSlot?.tool_definition) {
        return {
          kind: 'rejected',
          reason: `Slot T${String(args.from_tool_number).padStart(2, '0')} on ${args.machine_id} has no tool assigned.`,
        };
      }
      const fromId = fromSlot.tool_definition.tool_id;
      if (toSlot?.tool_definition) {
        const toId = toSlot.tool_definition.tool_id;
        await getApiClient().assignToolToSlot(ctx.shopId, args.machine_id, args.to_tool_number, fromId);
        await getApiClient().assignToolToSlot(ctx.shopId, args.machine_id, args.from_tool_number, toId);
      } else {
        await getApiClient().assignToolToSlot(ctx.shopId, args.machine_id, args.to_tool_number, fromId);
        await getApiClient().unassignToolFromSlot(ctx.shopId, args.machine_id, args.from_tool_number);
      }
      return { kind: 'swapped_slots', machineId: args.machine_id, fromNum: args.from_tool_number, toNum: args.to_tool_number };
    }

    case 'unsupported_request': {
      const args = intent.args as unknown as UnsupportedArgs;
      return { kind: 'rejected', reason: args.reason ?? 'Request not supported.' };
    }

    case 'query_machine_tools':
    case 'query_tools_needing_attention':
    case 'general_answer':
      return { kind: 'rejected', reason: 'Query intents are handled client-side and should not reach executeIntent.' };

    default: {
      const exhaustive: never = intent.tool;
      return { kind: 'rejected', reason: `Unknown tool: ${String(exhaustive)}` };
    }
  }
}

function normalizeCreate(args: CreateToolLibraryArgs): CreateToolLibraryInput {
  if (!TOOL_TYPE_SET.has(args.type)) {
    throw new Error(`Tool type "${args.type}" is not supported. Supported: ${TOOL_TYPES.join(', ')}.`);
  }
  if (!TOOL_MATERIAL_SET.has(args.material)) {
    throw new Error(`Tool material "${args.material}" is not supported.`);
  }
  return {
    description:           args.description.trim(),
    type:                  args.type as ToolType,
    material:              args.material as ToolMaterial,
    manufacturer:          args.manufacturer ?? null,
    part_number:           args.part_number ?? null,
    diameter_mm:           args.diameter_mm ?? null,
    expected_life_cuts:    args.expected_life_cuts ?? null,
    expected_life_minutes: args.expected_life_minutes ?? null,
    notes:                 args.notes ?? null,
  };
}

/** Returns the single matching library entry, or null if 0 or >1 match. */
function matchLibraryTool(
  hint: string | null | undefined,
  library: readonly ToolLibraryRecord[] | undefined,
): ToolLibraryRecord | null {
  if (!hint || !library || library.length === 0) return null;
  const needle = hint.toLowerCase();
  const matches = library.filter(
    (l) =>
      l.description.toLowerCase().includes(needle) ||
      needle.includes(l.description.toLowerCase()),
  );
  return matches.length === 1 ? (matches[0] ?? null) : null;
}

/** Returns the library ID of the single matching entry, or null. */
function matchLibrary(
  hint: string | null | undefined,
  library: readonly ToolLibraryRecord[] | undefined,
): string | null {
  return matchLibraryTool(hint, library)?.tool_id ?? null;
}
